import { Dialog, IconButton, LuIcon, Separator, Text, stopPropagation } from "@overmap-ai/blocks"
import { type IssueType, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfIssueType } from "@overmap-ai/core"
import { EditorContent, useEditor } from "@tiptap/react"
import { memo } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import {
	BASE_EXTENSIONS,
	IssueTypeAttachmentNode,
	LINK_NODES,
	MENTION_EXTENSIONS,
	useEditorContent,
} from "../../tiptap"
import { EditorContext } from "../../tiptap/context"
import { IssueTypeFileHandler } from "../../tiptap/extensions"
import { AttachmentList } from "../AttachmentList/AttachmentList"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import type { ExtendableDialogProps } from "./typings"

interface IssueTypeDescriptionDialogProps extends ExtendableDialogProps {
	issueType: Stored<IssueType>
}

export const IssueTypeDescriptionDialog = memo<IssueTypeDescriptionDialogProps>((props) => {
	const {
		issueType,
		children,
		open,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		defaultOpen,
		...rest
	} = props

	const issueTypeAttachments = useShallowEqaulSelector(selectAttachmentsOfIssueType(issueType.uuid))

	const editorContent = useEditorContent(issueType.description)

	const editor = useEditor(
		{
			content: editorContent,
			editable: false,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				IssueTypeAttachmentNode,
				IssueTypeFileHandler.configure({
					uuid: issueType.uuid,
				}),
			],
		},
		[issueType.uuid],
	)

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal} defaultOpen={defaultOpen}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content className="flex flex-col gap-2" onClick={stopPropagation} {...rest}>
				<div className="flex justify-between gap-2">
					<div className="flex items-center gap-2">
						<IssueTypeIcon issueTypeId={issueType.uuid} />
						<Dialog.Title asChild>
							<Text>{issueType.name}</Text>
						</Dialog.Title>
					</div>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base" size="sm">
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>

				<Separator size="full" />

				<div className="flex w-full grow flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<EditorContext value={editor}>
						<EditorContent className="w-full grow" editor={editor} />
					</EditorContext>

					<AttachmentList attachments={issueTypeAttachments} service={sdk.issueTypeAttachments} />
				</div>
			</Dialog.Content>
		</Dialog.Root>
	)
})

IssueTypeDescriptionDialog.displayName = "IssueTypeDescriptionDialog"
